import { z } from '@customerio/hydra-validation';
import { getApiClientErrorBody, isApiClientError } from '@/services/api/errors';

/**
 * How the add-person form responds to a rejected create:
 * - `suppressed`: the id/email is on the suppression list — a banner, not a field error.
 * - `attribute`: a per-attribute detail (a duplicate, an invalid format) shown under that row.
 * - `coded`: an error the backend labels with a code, shown as a snackbar.
 *
 * Anything else — a non-API failure, or an API error the form has no place
 * for — classifies as `null`, and the caller reports the error as it stands.
 */
export type CreatePersonError =
  | { kind: 'suppressed'; attribute: string }
  | { kind: 'attribute'; attribute: string; detail: string }
  | { kind: 'coded'; code: string; detail: string };

/** The fields of a JSON:API error entry the form branches on; everything is optional on the wire. */
const ErrorEntrySchema = z.object({
  status: z.union([z.string(), z.number()]).optional(),
  detail: z.string().optional(),
  code: z.string().nullable().optional(),
  source: z.object({ pointer: z.string().optional() }).optional(),
});

const ErrorBodySchema = z.object({ errors: z.array(ErrorEntrySchema) });

type ErrorEntry = z.infer<typeof ErrorEntrySchema>;

const NO_ERROR: ErrorEntry = {};

/** The first entry of a `{ errors: [...] }` body, or an empty entry when the body has another shape. */
function firstError(body: unknown): ErrorEntry {
  const parsed = ErrorBodySchema.safeParse(body);
  return parsed.success ? (parsed.data.errors[0] ?? NO_ERROR) : NO_ERROR;
}

const ATTRIBUTE_POINTER_PREFIX = '/data/attributes/';

/** The legacy 400 replies name the attribute in the message rather than in a pointer. */
function classifyLegacyError(detail: string, pointer: string | undefined): CreatePersonError | null {
  if (detail.toLowerCase().includes('suppressed')) {
    const attribute = detail.toLowerCase().includes('email') || pointer?.includes('email') === true ? 'email' : 'id';
    return { kind: 'suppressed', attribute };
  }
  if (detail.startsWith('A person with this email')) {
    return { kind: 'attribute', attribute: 'email', detail };
  }
  if (detail.startsWith('A person with this id')) {
    return { kind: 'attribute', attribute: 'id', detail };
  }
  return null;
}

function classifyPointerError(detail: string, pointer: string): CreatePersonError {
  const attribute = pointer.replace(ATTRIBUTE_POINTER_PREFIX, '');
  if (detail.toLowerCase().includes('suppressed')) {
    return { kind: 'suppressed', attribute };
  }
  return { kind: 'attribute', attribute, detail };
}

/**
 * Classifies a rejected create. Returns `null` for anything the form cannot
 * place, which the caller reports as it stands.
 *
 * Suppression is detected from the message wording, as the API has no code for it.
 */
export function classifyCreatePersonError(error: unknown): CreatePersonError | null {
  if (!isApiClientError(error)) {
    return null;
  }

  const { status, detail = '', code, source } = firstError(getApiClientErrorBody(error));
  const pointer = source?.pointer;

  if (String(status) === '400') {
    const legacy = classifyLegacyError(detail, pointer);
    if (legacy != null) {
      return legacy;
    }
  } else if (pointer != null) {
    return classifyPointerError(detail, pointer);
  }

  if (code != null) {
    return { kind: 'coded', code, detail };
  }
  return null;
}
